import React from 'react';
import { Box, Button, Chip, IconButton, LinearProgress, Tooltip, Typography, useTheme } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { formatBytes, getVideoId, IStoryViewerStory, renditionUrl, thumbnailUrl } from '../../lib/storyViewer';
import { useVideoDownload } from './useVideoDownload';

interface IProps {
    story: IStoryViewerStory;
    /** True while this tile is playing its video inline. Only one tile plays at a time. */
    isPlaying: boolean;
    onOpen: (story: IStoryViewerStory) => void;
    onPlay: (story: IStoryViewerStory) => void;
    onStop: () => void;
}

/** Inline tiles are small, so start with 720p and only fall back to heavier renditions if it is missing. */
const TILE_RESOLUTIONS = [720, 480, 360, 240, 1080, 1440, 2160];

export const formatStoryDate = (value?: string | null) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
};

export const storyFileBase = (story: IStoryViewerStory, suffix = '') => `story-${story.storyId}${suffix}`;

export const storyPoster = (story: IStoryViewerStory): string | null => {
    if (story.imageLink) return story.imageLink;
    const videoId = getVideoId(story.videoLink);
    return videoId ? thumbnailUrl(videoId) : null;
};

export const storySubtitle = (story: IStoryViewerStory) =>
    [story.recipient ? `For ${story.recipient}` : null, story.product].filter(Boolean).join(' · ');

const MAX_TAGS = 4;

const stop = (event: React.SyntheticEvent) => event.stopPropagation();

const StoryCard = ({ story, isPlaying, onOpen, onPlay, onStop }: IProps) => {
    const theme = useTheme();
    const download = useVideoDownload();
    const poster = storyPoster(story);
    const videoId = getVideoId(story.videoLink);
    const hasVideo = Boolean(videoId);
    const tags = story.tags || [];
    const visibleTags = tags.slice(0, MAX_TAGS);
    const hiddenTags = tags.length - visibleTags.length;
    const { state, busy } = download;
    const percent = state.progress && state.progress.total > 0
        ? Math.min(100, Math.round((state.progress.loaded / state.progress.total) * 100))
        : null;

    const handleDownload = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (!story.videoLink || busy) return;
        download.start(story.videoLink, storyFileBase(story));
    };

    const handlePlay = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (hasVideo) onPlay(story);
    };

    const handleStop = (event: React.SyntheticEvent) => {
        event.stopPropagation();
        onStop();
    };

    return (
        <Box
            role="button"
            tabIndex={0}
            aria-label={`Open story ${story.storyId}`}
            onClick={() => onOpen(story)}
            onKeyDown={(event) => {
                if (event.target !== event.currentTarget) return;
                if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onOpen(story); }
            }}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                cursor: 'pointer',
                outline: 'none',
                transition: 'box-shadow .2s, transform .2s',
                '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' },
                '&:focus-visible': { boxShadow: `0 0 0 2px ${theme.palette.primary.main}` },
            }}
        >
            {/* Fixed 9:16 media area: poster and inline video are absolutely positioned so content can never stretch the tile. */}
            <Box sx={{ position: 'relative', width: '100%', aspectRatio: '9 / 16', overflow: 'hidden', flexShrink: 0, bgcolor: theme.palette.mode === 'dark' ? '#000' : '#e9edf0' }}>
                {isPlaying && videoId ? (
                    <Box onClick={stop} onKeyDown={stop} sx={{ position: 'absolute', inset: 0, bgcolor: '#000', cursor: 'default' }}>
                        <video
                            key={videoId}
                            autoPlay
                            controls
                            playsInline
                            poster={poster || undefined}
                            onEnded={onStop}
                            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        >
                            {TILE_RESOLUTIONS.map((resolution) => (
                                <source key={resolution} src={`${renditionUrl(videoId, resolution)}#t=0.001`} type="video/mp4" />
                            ))}
                        </video>
                        <IconButton
                            size="small"
                            aria-label="Stop video"
                            onClick={handleStop}
                            sx={{ position: 'absolute', top: 6, right: 6, bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' } }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                ) : (
                    <>
                        {poster ? (
                            <img
                                src={poster}
                                alt={`Story ${story.storyId}`}
                                loading="lazy"
                                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                        ) : (
                            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', fontSize: 13 }}>
                                No preview
                            </Box>
                        )}
                        {hasVideo && (
                            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,0,0,0.2)' }}>
                                <Tooltip title="Play here" arrow>
                                    <IconButton
                                        aria-label="Play video in tile"
                                        onClick={handlePlay}
                                        sx={{ p: 0.5, color: '#fff', filter: 'drop-shadow(0 2px 6px rgba(0,0,0,.5))', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)', transform: 'scale(1.08)' }, transition: 'transform .15s' }}
                                    >
                                        <PlayCircleOutlineIcon sx={{ fontSize: 56, opacity: 0.92 }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}
                        {story.occasion && (
                            <Chip
                                size="small"
                                label={story.occasion}
                                sx={{ position: 'absolute', top: 8, left: 8, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', fontWeight: 600, backdropFilter: 'blur(2px)', pointerEvents: 'none' }}
                            />
                        )}
                        <Chip
                            size="small"
                            label={`#${story.storyId}`}
                            sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', backdropFilter: 'blur(2px)', pointerEvents: 'none' }}
                        />
                    </>
                )}
            </Box>
            <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75, flex: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {storySubtitle(story) || 'Story'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        {formatStoryDate(story.date)}
                    </Typography>
                </Box>
                {tags.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {visibleTags.map((tag) => (
                            <Tooltip key={`${tag.categoryId}-${tag.tagId}`} title={tag.category} arrow>
                                <Chip size="small" label={tag.tagName} variant="outlined" sx={{ height: 22, fontSize: 11 }} />
                            </Tooltip>
                        ))}
                        {hiddenTags > 0 && (
                            <Chip size="small" label={`+${hiddenTags}`} sx={{ height: 22, fontSize: 11 }} />
                        )}
                    </Box>
                )}
                <Box sx={{ mt: 'auto', pt: 0.5 }} onClick={stop}>
                    {busy ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'default' }}>
                            <Box sx={{ flex: 1 }}>
                                <LinearProgress variant={percent === null ? 'indeterminate' : 'determinate'} value={percent ?? 0} sx={{ borderRadius: 1 }} />
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {state.status === 'probing'
                                        ? 'Finding best quality…'
                                        : `${percent ?? 0}%${state.progress?.total ? ` of ${formatBytes(state.progress.total)}` : ''}`}
                                </Typography>
                            </Box>
                            <IconButton size="small" aria-label="Cancel download" onClick={(event) => { event.stopPropagation(); download.cancel(); }}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    ) : (
                        <Button
                            fullWidth
                            size="small"
                            variant="contained"
                            startIcon={<DownloadIcon />}
                            disabled={!hasVideo}
                            onClick={handleDownload}
                        >
                            {hasVideo ? 'Download' : 'No video'}
                        </Button>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default StoryCard;
